import { Expose } from 'class-transformer';
import { differenceInDays, parseISO } from 'date-fns';
import { maxBy, minBy } from 'es-toolkit/array';
import { PccAdtRecord } from './adt-record.typings';
import { PccAdvanceDirective } from './advance-directive.typings';
import { PccAllergy } from './allergy.typings';
import { PccCondition } from './condition.typings';
import { PccCoverage } from './coverage.typings';
import { PccImmunization } from './immunization.typings';
import { PccPatient } from './patient.typings';
import { PccPharmacy } from './pharmacy.typings';
import { PccPractitioner } from './practitioner.typings';

/**
 * Aggregated profile page data from multiple PCC endpoints
 * Used for generating the patient profile page PDF
 */
export class PccProfilePageData {
	/**
	 * Patient details including contacts and addresses
	 */
	patient: PccPatient;

	/**
	 * Patient's insurance coverage information
	 */
	coverages: PccCoverage;

	/**
	 * Array of patient allergies
	 */
	allergies: PccAllergy[];

	/**
	 * Array of ADT (Admission/Discharge/Transfer) records
	 */
	adtRecords: PccAdtRecord[];

	/**
	 * Array of practitioners/care providers for the patient
	 */
	practitioners: PccPractitioner[];

	/**
	 * Array of patient conditions/diagnoses
	 */
	conditions: PccCondition[];

	/**
	 * Array of pharmacies associated with patient medications
	 */
	pharmacies: PccPharmacy[];

	/**
	 * Array of patient immunization records
	 */
	immunizations: PccImmunization[];

	/**
	 * Array of advance directives
	 */
	advanceDirectives: PccAdvanceDirective[];

	/**
	 * ISO timestamp when this data was generated
	 */
	generatedAt: string;

	/**
	 * Base64 encoded patient photo (data:image/jpeg;base64,...)
	 */
	patientPhoto?: string;

	@Expose()
	get allergiesDisplay(): string {
		if (!this.allergies?.length) return '';
		const outlier = this.allergies.find((a) => a.outliers);
		if (outlier) return outlier.outliers;
		return this.allergies.map((a) => a.allergen).filter((val) => val).join(', ');
	}

	@Expose()
	get mostRecentAdmission(): PccAdtRecord | null {
		const admissions = this.adtRecords?.filter((r) => r.standardActionType === 'Admission');
		return maxBy(admissions, (admission) => parseISO(admission.effectiveDateTime)?.getTime());
	}

	@Expose()
	get mostRecentDischarge(): PccAdtRecord | null {
		const discharges = this.adtRecords?.filter((r) => r.standardActionType === 'Discharge');
		return maxBy(discharges, (discharge) => parseISO(discharge.effectiveDateTime)?.getTime());
	}

	@Expose()
	get earliestAdmission(): PccAdtRecord | null {
		const admissions = this.adtRecords?.filter((r) => r.standardActionType === 'Admission');
		return minBy(admissions, (addmission) => parseISO(addmission.effectiveDateTime)?.getTime());
	}

	@Expose()
	get lengthOfStay(): number | null {
		const admission = this.mostRecentAdmission;
		const discharge = this.mostRecentDischarge;
		if (!admission?.effectiveDateTime || !discharge?.effectiveDateTime) return null;
		const days = differenceInDays(parseISO(discharge.effectiveDateTime), parseISO(admission.effectiveDateTime));
		return days >= 0 ? days : null;
	}

	constructor(partial?: Partial<PccProfilePageData>) {
		if (partial) Object.assign(this, partial);
		this.generatedAt = this.generatedAt || new Date().toISOString();
	}
}
